import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Plus, Plane, Trash2, MapPin, GraduationCap, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const APP_STATUSES = [
  { value: "draft",         label: "Draft",         color: "#94A3B8" },
  { value: "docs_pending",  label: "Docs pending",  color: "#F59E0B" },
  { value: "submitted",     label: "Submitted",     color: "#0EA5FF" },
  { value: "interview",     label: "Interview",     color: "#8B5CF6" },
  { value: "offer",         label: "Offer",         color: "#5FBF3A" },
  { value: "visa",          label: "Visa",          color: "#0284C7" },
  { value: "travel",        label: "Travel",        color: "#14346E" },
  { value: "enrolled",      label: "Enrolled",      color: "#15803D" },
  { value: "rejected",      label: "Rejected",      color: "#EF4444" },
];
const VISA_STATUSES = ["not_started", "applied", "interview", "approved", "rejected"];

export default function StudyAbroad({ mbbsOnly = false }) {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (mbbsOnly) params.is_mbbs = true;
    else params.is_mbbs = false;
    if (statusFilter !== "all") params.status = statusFilter;
    const r = await api.get("/study-abroad/applications", { params });
    setItems(r.data);
  }, [statusFilter, mbbsOnly]);
  useEffect(() => { load(); }, [load]);

  const label = APP_STATUSES.reduce((acc, s) => { acc[s.value] = s; return acc; }, {});

  const title = mbbsOnly ? "MBBS Abroad" : "Study Abroad";

  return (
    <AppLayout title={title}>
      <div className="flex items-center gap-3 mb-5">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="sa-filter-status" className="w-44 bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {APP_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <NewApplicationDialog open={newOpen} setOpen={setNewOpen} mbbsOnly={mbbsOnly} onCreated={load}/>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full text-center py-16 text-[var(--miro-text-muted)]">
            No {mbbsOnly ? "MBBS" : "study abroad"} applications yet.
          </div>
        )}
        {items.map(a => {
          const st = label[a.status];
          const totalDocs = (a.docs || []).length;
          const doneDocs = (a.docs || []).filter(d => d.done).length;
          const pct = totalDocs ? Math.round((doneDocs / totalDocs) * 100) : 0;
          return (
            <div key={a.id} data-testid={`app-card-${a.id}`} className="miro-card p-5 cursor-pointer" onClick={() => setOpenId(a.id)}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="miro-overline flex items-center gap-1"><MapPin size={11}/> {a.country}</div>
                  <h3 className="font-display font-bold text-lg text-[var(--miro-navy)] mt-1 leading-tight">{a.student_name}</h3>
                  <div className="text-sm text-[var(--miro-text-secondary)] mt-0.5 truncate">{a.university}</div>
                </div>
                <span className="text-[10px] px-2 py-1 rounded font-semibold uppercase tracking-wider text-white"
                      style={{ background: st?.color || "#64748B" }}>{st?.label}</span>
              </div>
              <div className="text-xs text-[var(--miro-text-muted)] mt-2 flex items-center gap-2">
                <GraduationCap size={12}/> {a.program || "—"} · {a.intake_season} {a.intake_year}
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-[11px] text-[var(--miro-text-muted)] mb-1">
                  <span className="flex items-center gap-1"><FileCheck2 size={11}/> Documents</span>
                  <span>{doneDocs}/{totalDocs}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[var(--miro-bg)] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#0EA5FF,#5FBF3A)" }}/>
                </div>
              </div>
              {a.visa_status && (
                <div className="mt-3 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded inline-block"
                     style={{ background: "#EFF6FF", color: "#0284C7" }}>
                  Visa: {a.visa_status.replace(/_/g," ")}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ApplicationDrawer appId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={load}/>
    </AppLayout>
  );
}

function ApplicationDrawer({ appId, open, onClose, onChanged }) {
  const [app, setApp] = useState(null);
  const load = useCallback(async () => {
    if (!appId) return;
    const r = await api.get(`/study-abroad/applications/${appId}`);
    setApp(r.data);
  }, [appId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async (patch) => {
    const r = await api.patch(`/study-abroad/applications/${appId}`, patch);
    setApp(r.data);
    onChanged?.();
  };

  const toggleDoc = async (name) => {
    const next = (app.docs || []).map(d => d.name === name ? { ...d, done: !d.done } : d);
    await save({ docs: next });
  };

  const remove = async () => {
    if (!window.confirm("Delete this application?")) return;
    await api.delete(`/study-abroad/applications/${appId}`);
    onChanged?.();
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="app-drawer" className="w-full sm:max-w-[560px] p-0 bg-white overflow-y-auto">
        <SheetTitle className="sr-only">Application detail</SheetTitle>
        {app && (
          <div>
            <div className="px-6 pt-6 pb-4 border-b border-[var(--miro-border)]">
              <div className="miro-overline flex items-center gap-1"><MapPin size={11}/> {app.country} · {app.university}</div>
              <SheetHeader className="mt-1">
                <SheetTitle className="font-display font-black text-2xl text-[var(--miro-navy)] tracking-tight">{app.student_name}</SheetTitle>
              </SheetHeader>
              <div className="flex items-center gap-2 mt-2 text-xs text-[var(--miro-text-secondary)]">
                <Plane size={12}/> {app.program || "—"} · {app.intake_season} {app.intake_year}
                <button data-testid="app-delete-btn" onClick={remove} className="ml-auto text-red-600 hover:text-red-800 flex items-center gap-1"><Trash2 size={13}/> Delete</button>
              </div>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <Label>Application status</Label>
                <Select value={app.status} onValueChange={(v) => save({ status: v })}>
                  <SelectTrigger data-testid="app-status-select" className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent>{APP_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Visa status</Label>
                <Select value={app.visa_status || "not_started"} onValueChange={(v) => save({ visa_status: v })}>
                  <SelectTrigger data-testid="app-visa-select" className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent>{VISA_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Travel date</Label>
                <Input data-testid="app-travel-date" type="date"
                       defaultValue={app.travel_date ? app.travel_date.slice(0,10) : ""}
                       onBlur={(e) => save({ travel_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                       className="mt-1"/>
              </div>
              <div>
                <Label>Documents checklist</Label>
                <div className="mt-2 rounded-lg border border-[var(--miro-border)] divide-y divide-[var(--miro-border)]">
                  {(app.docs || []).map(d => (
                    <label key={d.name} data-testid={`app-doc-${d.name}`}
                           className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer hover:bg-[var(--miro-bg)]">
                      <Checkbox checked={d.done} onCheckedChange={() => toggleDoc(d.name)}/>
                      <span className={d.done ? "line-through text-[var(--miro-text-muted)]" : "text-[var(--miro-navy)] font-medium"}>{d.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea data-testid="app-notes" defaultValue={app.notes || ""}
                          onBlur={(e) => save({ notes: e.target.value })} rows={3} className="mt-1"/>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function NewApplicationDialog({ open, setOpen, mbbsOnly, onCreated }) {
  const [f, setF] = useState({
    student_name: "", country: "", university: "", program: "",
    intake_year: new Date().getFullYear() + 1, intake_season: "fall", status: "draft",
    is_mbbs: mbbsOnly, notes: "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  useEffect(() => { upd("is_mbbs", mbbsOnly); }, [mbbsOnly]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/study-abroad/applications", { ...f, intake_year: Number(f.intake_year) });
      toast.success("Application created");
      setOpen(false);
      setF({ ...f, student_name: "", country: "", university: "", program: "", notes: "" });
      onCreated?.();
    } catch (e) { toast.error("Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-app-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New application</Button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New {mbbsOnly ? "MBBS" : "Study Abroad"} application</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Student name *</Label><Input data-testid="new-app-student" value={f.student_name} onChange={e=>upd("student_name",e.target.value)} className="mt-1"/></div>
          <div><Label>Country *</Label><Input data-testid="new-app-country" value={f.country} onChange={e=>upd("country",e.target.value)} className="mt-1"/></div>
          <div><Label>University *</Label><Input data-testid="new-app-university" value={f.university} onChange={e=>upd("university",e.target.value)} className="mt-1"/></div>
          <div><Label>Program</Label><Input data-testid="new-app-program" value={f.program} onChange={e=>upd("program",e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Intake season</Label>
            <Select value={f.intake_season} onValueChange={v=>upd("intake_season",v)}>
              <SelectTrigger data-testid="new-app-season" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="spring">Spring</SelectItem>
                <SelectItem value="summer">Summer</SelectItem>
                <SelectItem value="fall">Fall</SelectItem>
                <SelectItem value="winter">Winter</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Intake year</Label><Input data-testid="new-app-year" type="number" value={f.intake_year} onChange={e=>upd("intake_year",e.target.value)} className="mt-1"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button data-testid="new-app-submit"
                  disabled={!f.student_name.trim() || !f.country.trim() || !f.university.trim() || saving}
                  onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
